import * as React from 'react';
import { Button } from 'reactstrap';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Icon } from '@app/Components/Icon';
import './self-test-badge.scss';

type SelfTestBadgeProps = React.PropsWithChildren<{
    loading?: boolean;
    loadingIcon?: IconName;
    loadingWithText?: boolean;
}>;

const WIDTH_TRANSITION_DURATION_MS = 250;

const SELF_TEST_BTN_BORDER_RADIUS_PX = 3.2;
const PLACEHOLDER_BORDER_WIDTH_PX = 1;
const RING_RADIUS_PX = SELF_TEST_BTN_BORDER_RADIUS_PX - PLACEHOLDER_BORDER_WIDTH_PX;
const RING_NORMALIZED_PATH_LENGTH = 100;

export class SelfTestBadge extends React.PureComponent<SelfTestBadgeProps, {}> {
    private readonly _ref: React.RefObject<HTMLDivElement> = React.createRef();
    private _widthTransitionTimeout?: number;

    componentWillUnmount() {
        window.clearTimeout(this._widthTransitionTimeout);
    }

    getSnapshotBeforeUpdate(prevProps: SelfTestBadgeProps) {
        if (prevProps.loading === this.props.loading || !this._ref.current) return null;
        return this._ref.current.getBoundingClientRect().width;
    }

    componentDidUpdate(prevProps: SelfTestBadgeProps, prevState: {}, widthBeforeUpdate: number | null) {
        const el = this._ref.current;
        if (widthBeforeUpdate === null || !el) return;

        const targetWidth = el.getBoundingClientRect().width;
        el.style.width = widthBeforeUpdate + 'px';
        void el.offsetWidth; // force a reflow so the browser registers the starting width before animating to the target
        el.style.width = targetWidth + 'px';

        window.clearTimeout(this._widthTransitionTimeout);
        this._widthTransitionTimeout = window.setTimeout(() => {
            if (this._ref.current) this._ref.current.style.width = '';
        }, WIDTH_TRANSITION_DURATION_MS);
    }

    render() {
        const { loading, children } = this.props;
        return (
            <div className="self-test-badge" ref={this._ref}>
                {loading ? this._renderPlaceholder() : children}
            </div>
        );
    }

    private _renderPlaceholder() {
        const { loadingIcon, loadingWithText } = this.props;
        return (
            <Button size="sm" disabled
                className="self-test-button self-test-badge-placeholder">
                <svg className="self-test-badge-placeholder-ring">
                    <rect x="0" y="0" width="100%" height="100%"
                        rx={RING_RADIUS_PX} ry={RING_RADIUS_PX} pathLength={RING_NORMALIZED_PATH_LENGTH} />
                </svg>
                <span className="self-test-content">
                    {loadingIcon && <Icon name={loadingIcon} />}
                    {loadingWithText && <span className="self-test-badge-placeholder-text" />}
                </span>
            </Button>
        );
    }
}
